import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { ThresholdLabel } from '@sorare/core/src/atoms/ui/SplitProgressBar/ThresholdLabel';

import type { CollectionBonusProgressBar_cardCollection } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  align-items: flex-start;
`;
const BarWrapper = styled(Vertical).attrs({ center: true })`
  padding-top: var(--half-unit);
  flex: 1;
`;

type Props = {
  cardCollection: Nullable<CollectionBonusProgressBar_cardCollection>;
  score: number;
  onAnimationEnd: () => void;
};
export const CollectionBonusProgressBar = ({
  cardCollection,
  score,
  onAnimationEnd,
}: Props) => {
  const { bonusThresholds } = cardCollection || {};

  if (!bonusThresholds?.length) {
    return null;
  }

  const nextBonusThresholdIndex = bonusThresholds.findIndex(
    bt => bt.threshold >= score
  );

  const nextBonusThreshold =
    nextBonusThresholdIndex !== -1
      ? bonusThresholds[nextBonusThresholdIndex]
      : bonusThresholds[bonusThresholds.length - 1];
  const previousBonusThreshold =
    nextBonusThresholdIndex !== -1
      ? bonusThresholds[nextBonusThresholdIndex - 1] || {
          threshold: 0,
          bonus: 0,
        }
      : bonusThresholds[bonusThresholds.length - 2] || {
          threshold: 0,
          bonus: 0,
        };

  const progress = score;
  const nextThresholdCrossed = score >= nextBonusThreshold.threshold;

  return (
    <Root>
      <ThresholdLabel
        shiny
        faded={nextThresholdCrossed}
        bonus={previousBonusThreshold.bonus}
      />
      <BarWrapper>
        <Progress
          style={
            {
              '--progress-color':
                'linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)',
            } as CSSProperties
          }
          value={progress - previousBonusThreshold.threshold}
          max={nextBonusThreshold.threshold - previousBonusThreshold.threshold}
          onAnimationEnd={onAnimationEnd}
          skipStartAnimation
        />
      </BarWrapper>
      <ThresholdLabel
        shiny={nextThresholdCrossed}
        bonus={nextBonusThreshold.bonus}
      />
    </Root>
  );
};

CollectionBonusProgressBar.fragments = {
  cardCollection: gql`
    fragment CollectionBonusProgressBar_cardCollection on CardCollection {
      slug
      bonusThresholds {
        threshold
        bonus
      }
    }
  ` as TypedDocumentNode<CollectionBonusProgressBar_cardCollection>,
};
